// AA "Landing Pages" report table (GA4 Traffic band, reference screenshot
// 2026-08-09) — 8 columns, every metric cell carrying its own real
// period-over-period ▲▼% badge (LandingPageRow.*_change_pct, matched by the
// exact landing_page string — see that interface's backend docstring).
//
// Modeled on AdEntityBreakdownTable's table shell (sticky header, its OWN
// overflow-x-auto scroll container so a half-width panel never blows out
// the page body — 8 badge-carrying columns will not fit at half width) but
// simpler: no tabs, no client-side pagination. AA's own report page just
// shows one scrollable page of up to 50 rows; the backend already caps
// top_landing_pages there.
//
// `totalCount`/`capped` (TrafficSection.total_landing_pages /
// landing_pages_capped) power the "Showing 50 of 1,337" footer (house
// wording -- no "Rows" unit word; the "+" only appears when `capped` is
// true, meaning the total itself is a floor, not an exact count) —
// rendering `rows.length` as the grand total would silently imply the
// visible 50 are the complete set, which they never are once a client has
// more than 50 landing pages. When the true total isn't known yet (the
// paginated count pull hasn't run for this client), the footer falls back
// to just the visible row count rather than fabricating a number.

import { useMemo, useState } from "react";
import type { LandingPageRow } from "@/types/clientAnalytics";
import {
  ChangePctBadge,
  formatDurationLong,
  formatNum,
} from "@/components/client-analytics/primitives";

export interface LandingPagesTableProps {
  rows: LandingPageRow[];
  totalCount?: number | null;
  capped?: boolean;
  emptyMessage?: string;
}

interface ColumnDef {
  label: string;
  format: (row: LandingPageRow) => string;
  changePct: (row: LandingPageRow) => number | null | undefined;
}

// key_events/total_purchasers render with 2 decimals ("34.00", "3.00") —
// matching AA's own reference exactly. Both are raw floats on the backend
// (GA4's conversions/purchasers metrics can be fractional under certain
// attribution models), so this isn't cosmetic rounding of a whole count.
const COLUMNS: ColumnDef[] = [
  { label: "Sessions", format: (r) => formatNum(r.sessions), changePct: (r) => r.sessions_change_pct },
  {
    label: "Total Users",
    format: (r) => formatNum(r.total_users),
    changePct: (r) => r.total_users_change_pct,
  },
  {
    label: "User Engagement",
    format: (r) => formatDurationLong(r.engagement_duration_seconds),
    changePct: (r) => r.engagement_duration_change_pct,
  },
  { label: "Views", format: (r) => formatNum(r.views), changePct: (r) => r.views_change_pct },
  {
    label: "Key Events",
    format: (r) => r.key_events.toFixed(2),
    changePct: (r) => r.key_events_change_pct,
  },
  {
    label: "Event Count",
    format: (r) => formatNum(r.event_count),
    changePct: (r) => r.event_count_change_pct,
  },
  {
    label: "Total Purchasers",
    format: (r) => r.total_purchasers.toFixed(2),
    changePct: (r) => r.total_purchasers_change_pct,
  },
];

export function LandingPagesTable({
  rows,
  totalCount,
  capped = false,
  emptyMessage = "Not enough traffic data yet.",
}: LandingPagesTableProps) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? rows.filter((r) => r.landing_page.toLowerCase().includes(term)) : rows;
  }, [rows, search]);

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <div className="text-dash-context text-muted-foreground">
          Showing {formatNum(filtered.length)}
          {totalCount != null && (
            <>
              {" "}
              of {formatNum(totalCount)}
              {/* Keep the "+" -- it means the total is a floor, not an
                  exact count, which is real information the caption would
                  otherwise lose. House wording drops only the "Rows" unit
                  word. */}
              {capped ? "+" : ""}
            </>
          )}
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search…"
          aria-label="Search landing pages"
          className="w-48 rounded-md border border-border bg-background px-2.5 py-1 text-dash-context text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>
      {rows.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-dash-context text-muted-foreground">
          {emptyMessage}
        </div>
      ) : (
        // This panel's own scroll container (both axes) — 8 badge-carrying
        // columns plus the landing-page column will not fit at half
        // viewport width; the page body must never scroll horizontally.
        <div className="max-h-[480px] overflow-y-auto overflow-x-auto">
          <table className="w-full border-collapse text-dash-subtitle">
            <thead className="sticky top-0 z-10 bg-muted">
              <tr className="border-b border-border">
                <th
                  scope="col"
                  className="px-4 py-2.5 text-left text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap"
                >
                  Landing Page
                </th>
                {COLUMNS.map((col) => (
                  <th
                    key={col.label}
                    scope="col"
                    className="px-4 py-2.5 text-right text-dash-label font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={COLUMNS.length + 1}
                    className="px-4 py-6 text-center text-dash-context text-muted-foreground"
                  >
                    No landing pages match your search.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr
                    key={row.landing_page}
                    className="border-b border-border last:border-0 transition-colors hover:bg-muted/60"
                  >
                    <td
                      className="px-4 py-2.5 max-w-[280px] truncate font-medium text-foreground"
                      title={row.landing_page}
                    >
                      {row.landing_page}
                    </td>
                    {COLUMNS.map((col) => (
                      <td key={col.label} className="px-4 py-2.5 text-right">
                        <span className="inline-flex items-center justify-end gap-1.5 tabular whitespace-nowrap">
                          <span className="text-foreground">{col.format(row)}</span>
                          <ChangePctBadge pct={col.changePct(row)} />
                        </span>
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
